// Customers › Memberships in Blueprint (pages/ux-full customers/memberships), on ui-web's ListPage
// (patterns/README.md, BL-189): the members, the visits their plans include that are coming due, the
// renewals coming up, and a way to the plans (Setup's now). The parts are the title block's tabs, Renewals
// with how many are at risk beside it. Over the same actions as Memberships.tsx (which Riverstone keeps):
// memberships.list a page at a time (member-pages.ts), recurringServices.due, memberships.expire; a
// member's sheet (MemberDetail.tsx) and Sell a membership (SellMembership.tsx) open over it. Each list is
// a DataTable whose region says its own loading, empty and failed; the members' figures are the title
// block's. While the company mirrors its import source, every part reads and none offers a change (BL-088).
import { useEffect, useState, type ReactNode } from 'react';
import { Button, DataTable, Figures, Link, Notice, type DataColumn } from '@flowology/ui-web/fl';
import {
  ListPage,
  ListPick,
  Section,
  type ListRegion,
  type ListStatus,
  type PageHeadProps,
} from '@flowology/ui-web/patterns';
import { BOOKER_PERMS, PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { dayText, todayIn } from '../books/model.ts';
import { errorText, useQuery, useToast } from '../books/ui.tsx';
import { keepsCards } from '../history/cards-model.ts';
import { client } from '../api.ts';
import { useDocFocus } from '../shared/doc-link.ts';
import { holdsAny } from '../shared/may.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { MemberDetail, StatusWord } from './MemberDetail.tsx';
import { SkipVisit } from './MemberStatus.tsx';
import { useMemberPages, useReadTo, type MemberPages } from './member-pages.ts';
import {
  AT_RISK_INPUT,
  atRiskCount,
  BILL_AT_RISK_NOTE,
  BILL_WORDS,
  DUE_DAYS,
  EXPIRE_PERMS,
  expiredText,
  addDaysTo,
  memberFacts,
  MEMBERSHIP_SELL_PERMS,
  membersInput,
  RENEWAL_DAYS,
  RENEWAL_HINT,
  RENEWAL_WORDS,
  renewingInput,
  shownText,
  sinceText,
  STATUS_PERMS,
  visitsText,
  whenText,
  wordTone,
  type Member,
  type MemberShow,
} from './model.ts';
import { openSellMembership } from './SellMembership.tsx';
import { onMembershipSold } from './sold.ts';
import './memberships.css';

export type MembersPart = 'members' | 'due' | 'renewals' | 'plans';

/** Visits due's line for those who don't book: the list, without sending them to To book. */
const DUE_LEDE_READ = `Included visits coming due in the next ${DUE_DAYS} days. The office books them from To book.`;

export const MEMBER_PARTS: { part: MembersPart; label: string; lede: string }[] = [
  {
    part: 'members',
    label: 'Members',
    lede: 'Everyone on a plan: what it includes, the visits used, and when it renews.',
  },
  {
    part: 'due',
    label: 'Visits due',
    lede: `Included visits coming due in the next ${DUE_DAYS} days. Book them from To book.`,
  },
  {
    part: 'renewals',
    label: 'Renewals',
    lede: `Renewals and bills that didn’t go through, to call first, then the plans that end in the next ${RENEWAL_DAYS} days.`,
  },
  {
    part: 'plans',
    label: 'Plans',
    lede: 'What each plan costs, how long it runs, the visits it includes and its member discount.',
  },
];

const SHOWS: { value: MemberShow; label: string }[] = [
  { value: 'active', label: 'Current' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'expired', label: 'Ended' },
  { value: 'at_risk', label: 'Renewal at risk' },
  { value: 'all', label: 'All' },
];

interface Due {
  id: string;
  recurringServiceId: string;
  membershipId: string;
  customerName: string;
  address: string;
  membershipName: string;
  jobTypeName: string;
  dueOn: string;
  visitsLeft: number;
}

/** A region's state from what's read: rows (or none yet) and the server's refusal. */
const stateOf = (rows: readonly unknown[] | null, error: string | null): ListStatus =>
  rows ? (rows.length ? 'ready' : 'empty') : error ? 'failed' : 'loading';

/** Under a list read a page at a time: the next page, how many of how many, and why it didn't read. */
const moreOf = (list: MemberPages) =>
  list.members && list.first && (list.more || list.busy)
    ? {
        label: `Load more (${shownText(list.members.length, list.first.total)})`,
        onPress: () => list.more?.(),
        isBusy: list.busy,
        error: list.error,
      }
    : null;

/** The member, as the row's way into their sheet (its name in strong, their address under it). */
const whoCell = (m: Member, open: (m: Member) => void) => ({
  look: 'strong' as const,
  text: (
    <span data-doc={m.id}>
      <Link label={`${m.customerName}’s ${m.planName} membership`} onPress={() => open(m)}>
        {m.customerName}
      </Link>
    </span>
  ),
  lines: [{ text: m.address }],
});

/** Where a renewal stands (BL-009), with the server's note and what can still be done, as words. */
const renewalLines = (m: Pick<Member, 'renewal' | 'billing'>, note: boolean) => {
  const out: ({ word: string; tone: ReturnType<typeof wordTone> } | { text: string })[] = [];
  if (m.renewal) {
    const [word, tone] = RENEWAL_WORDS[m.renewal.state];
    out.push({ word, tone: wordTone(tone) });
    if (note && m.renewal.note) out.push({ text: m.renewal.note });
    const hint = RENEWAL_HINT[m.renewal.state];
    if (note && hint) out.push({ text: hint });
  }
  if (m.billing?.atRisk) {
    const [word, tone] = BILL_WORDS.at_risk;
    out.push({ word, tone: wordTone(tone) });
    if (note) out.push({ text: BILL_AT_RISK_NOTE });
  }
  return out;
};

interface Props {
  today: string;
  toast: (m: string) => void;
  held: readonly string[];
  /** A notification's membership (BL-009): its row in view, on Renewals. */
  focus?: string;
  /** The title block every part shares (its crumb, title, lede and the parts as tabs). */
  head: PageHeadProps;
  /** The lines under it every part shares (the mirror's line, what was just done). */
  notice: ReactNode;
}

/** Who opens a member's sheet, and what they may do in it. */
function useSheet(held: readonly string[], reload: () => void, today: string) {
  const [open, setOpen] = useState<Member | null>(null);
  const sheet = open && (
    <MemberDetail
      member={open}
      today={today}
      // choosing the card needs the sale's grant and the customer's cards (customers.edit)
      canChooseCard={holdsAny(held, MEMBERSHIP_SELL_PERMS) && keepsCards(held)}
      canRenew={holdsAny(held, MEMBERSHIP_SELL_PERMS)}
      canManage={holdsAny(held, STATUS_PERMS)}
      onChanged={reload}
      onClose={() => setOpen(null)}
    />
  );
  return { setOpen, sheet };
}

/** Members: everyone on a plan, by status or a name or street, a page at a time; Sell a membership. */
function Members({ today, held, head, notice }: Props) {
  const [status, setStatus] = useState<MemberShow>('active');
  // a name or street, looked for by the server once typing stops: the member may not be on a page in hand
  const [find, setFind] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQuery(find.trim()), 250);
    return () => clearTimeout(t);
  }, [find]);
  // at risk: a renewal that didn't go through, whatever the membership's own status (BL-009)
  const q = useMemberPages(membersInput(status, query));
  // one sold from here (or anywhere) shows at once (QA-531)
  const reload = q.reload;
  useEffect(() => onMembershipSold(() => void reload()), [reload]);
  const { setOpen, sheet } = useSheet(held, () => void q.reload(), today);
  const columns: DataColumn<Member>[] = [
    { id: 'who', label: 'Member', cell: (m) => whoCell(m, setOpen) },
    { id: 'plan', label: 'Plan', cell: (m) => ({ text: m.planName }) },
    { id: 'since', label: 'Since', cell: (m) => ({ text: sinceText(m.startsOn) }) },
    {
      id: 'renews',
      label: 'Renews',
      cell: (m) => ({
        text: dayText(m.endsOn),
        lines: [
          ...(m.status === 'active' ? [{ text: whenText(m.endsOn, today) }] : []),
          ...renewalLines(
            { renewal: m.renewal?.state === 'scheduled' ? null : m.renewal, billing: m.billing },
            false,
          ),
        ],
      }),
    },
    { id: 'visits', label: 'Visits', cell: (m) => ({ text: visitsText(m.visitsUsed, m.visitsTotal) }) },
    {
      id: 'next',
      label: 'Next visit',
      cell: (m) => (m.nextDueOn ? { text: dayText(m.nextDueOn) } : { text: 'None', look: 'muted' }),
    },
    { id: 'status', label: 'Status', cell: (m) => ({ text: <StatusWord m={m} /> }) },
  ];
  const list: ListRegion = {
    title: 'Everyone on a plan',
    status: stateOf(q.members, q.error),
    what: 'the members',
    emptyText: query
      ? 'No member here by that name or street.'
      : status === 'active'
        ? 'No members yet. A membership is sold on a visit’s invoice, from the iPad or the office.'
        : status === 'at_risk'
          ? 'No renewal is at risk.'
          : 'No memberships here.',
    failedText: q.error ?? 'The members didn’t load.',
    onRetry: () => void q.reload(),
    children: q.members && <DataTable columns={columns} rows={q.members} more={moreOf(q)} empty="" />,
  };
  const f = q.first && memberFacts(q.first);
  return (
    <ListPage
      head={{
        ...head,
        ...(holdsAny(held, MEMBERSHIP_SELL_PERMS)
          ? { actions: <Button onPress={() => openSellMembership()}>Sell a membership</Button> }
          : {}),
        ...(f
          ? {
              figures: (
                // Blueprint's figures over the whole list, every page (DESIGN.md §3)
                <Figures
                  items={[
                    { label: 'Members', value: String(f.members) },
                    {
                      label: 'Visits used',
                      value: String(f.visitsUsed),
                      sub: `of ${f.visitsTotal} included`,
                    },
                    { label: 'Renew in six weeks', value: String(f.renewSoon) },
                    { label: 'Renew by themselves', value: String(f.autoRenew), sub: `of ${f.members}` },
                  ]}
                />
              ),
            }
          : {}),
      }}
      notice={notice}
      search={{ label: 'Find', value: find, onChange: setFind, placeholder: 'Name or street' }}
      filters={
        <ListPick label="Show" value={status} options={SHOWS} onChange={(v) => setStatus(v as MemberShow)} />
      }
      list={list}
      layers={sheet}
    />
  );
}

/** Visits due: the included visits coming due, to book (in To book) or skip. */
function VisitsDue({ today, held, toast, head, notice, onBook }: Props & { onBook: (() => void) | null }) {
  const q = useQuery<{ due: Omit<Due, 'id'>[] }>('recurringServices.due', {
    asOf: addDaysTo(today, DUE_DAYS),
  });
  // a visit the customer won't take: skipped, its share recognised (memberships.skipVisit, BL-056)
  const canSkip = holdsAny(held, STATUS_PERMS);
  const rows: Due[] | null = q.data ? q.data.due.map((d) => ({ ...d, id: d.recurringServiceId })) : null;
  const columns: DataColumn<Due>[] = [
    {
      id: 'due',
      label: 'Due',
      cell: (d) => ({
        text: dayText(d.dueOn),
        lines: [{ text: whenText(d.dueOn, today), ...(d.dueOn < today ? { tone: 'bad' as const } : {}) }],
      }),
    },
    {
      id: 'who',
      label: 'Member',
      cell: (d) => ({ look: 'strong', text: d.customerName, lines: [{ text: d.address }] }),
    },
    { id: 'plan', label: 'Plan', cell: (d) => ({ text: d.membershipName }) },
    { id: 'visit', label: 'Visit', cell: (d) => ({ text: d.jobTypeName }) },
    { id: 'left', label: 'Left this term', align: 'end', cell: (d) => ({ text: String(d.visitsLeft) }) },
    ...(canSkip
      ? [
          {
            id: 'skip',
            label: 'Skip',
            cell: (d: Due) => ({
              text: (
                <SkipVisit
                  recurringServiceId={d.recurringServiceId}
                  what={`${d.customerName}’s ${d.jobTypeName.toLowerCase()} due ${dayText(d.dueOn)}`}
                  onDone={(said) => {
                    toast(said);
                    void q.reload();
                  }}
                />
              ),
            }),
          },
        ]
      : []),
  ];
  const list: ListRegion = {
    title: 'Coming due',
    status: stateOf(rows, q.error),
    what: 'the visits due',
    emptyText: `No included visit is due in the next ${DUE_DAYS} days.`,
    failedText: q.error ?? 'The visits due didn’t load.',
    onRetry: () => void q.reload(),
    children: rows && <DataTable columns={columns} rows={rows} empty="" />,
  };
  // To book is the bookers' place; anyone else (the bookkeeper) sees the list without the way in
  return (
    <ListPage
      head={{
        ...head,
        ...(onBook ? { actions: <Button onPress={onBook}>Book them in To book</Button> } : {}),
      }}
      notice={notice}
      list={list}
    />
  );
}

/** Renewals that didn't go through (BL-009), who to call first: over Renewing soon while there are any. */
function AtRisk({ today, held, focus }: Props) {
  const q = useMemberPages(AT_RISK_INPUT);
  const { setOpen, sheet } = useSheet(held, () => void q.reload(), today);
  useReadTo(q, focus);
  useDocFocus(q.members?.some((m) => m.id === focus) ? focus : null, !!q.members);
  const columns: DataColumn<Member>[] = [
    { id: 'who', label: 'Member', cell: (m) => whoCell(m, setOpen) },
    { id: 'plan', label: 'Plan', cell: (m) => ({ text: m.planName }) },
    { id: 'ends', label: 'Term ends', cell: (m) => ({ text: dayText(m.endsOn) }) },
    {
      id: 'what',
      label: 'What happened',
      cell: (m) => {
        const [first, ...rest] = renewalLines(m, true);
        if (!first) return {};
        return 'word' in first ? { word: first, lines: rest } : { text: first.text, lines: rest };
      },
    },
  ];
  const status = stateOf(q.members, q.error);
  return (
    <Section title="At risk">
      {status === 'loading' ? (
        <p className="bp-member-quiet" role="status">
          Loading the renewals at risk…
        </p>
      ) : status === 'failed' ? (
        <p className="bp-member-bad" role="alert">
          <span>{q.error ?? 'The renewals at risk didn’t load.'}</span>{' '}
          <Link onPress={() => void q.reload()}>Try again</Link>
        </p>
      ) : (
        <DataTable columns={columns} rows={q.members ?? []} more={moreOf(q)} empty="No renewal is at risk." />
      )}
      {sheet}
    </Section>
  );
}

/** Renewals: those at risk first (while there are any), then renewing soon, by end date across every page,
 * the soonest first (list caps M6). */
function Renewals(props: Props & { atRisk: boolean }) {
  const { today, toast, held, focus, head, notice, atRisk } = props;
  const q = useMemberPages(renewingInput(today));
  // a declined, no-card or expiring-card notice is about one renewing soon (an at-risk one is above)
  useReadTo(q, focus);
  useDocFocus(q.members?.some((m) => m.id === focus) ? focus : null, !!q.members);
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState<string | null>(null);
  // memberships.expire: the plans past their end date end, and their unused revenue is recognised.
  const expire = async () => {
    setBusy(true);
    setWhy(null);
    try {
      const r = await client.call<{ expired: number }>('memberships.expire', { asOf: today });
      toast(expiredText(r.expired));
      q.reload();
    } catch (e) {
      setWhy(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const columns: DataColumn<Member>[] = [
    {
      id: 'renews',
      label: 'Renews',
      cell: (m) => ({
        text: <span data-doc={m.id}>{dayText(m.endsOn)}</span>,
        lines: [{ text: whenText(m.endsOn, today) }],
      }),
    },
    {
      id: 'who',
      label: 'Member',
      cell: (m) => ({ look: 'strong', text: m.customerName, lines: [{ text: m.address }] }),
    },
    { id: 'plan', label: 'Plan', cell: (m) => ({ text: m.planName }) },
    {
      id: 'renewal',
      label: 'Renewal',
      cell: (m) => {
        if (!m.renewal) return { text: m.autoRenew ? 'Renews by itself' : 'Ask them to renew' };
        const [first, ...rest] = renewalLines({ renewal: m.renewal, billing: null }, true);
        return { word: first as { word: string; tone: ReturnType<typeof wordTone> }, lines: rest };
      },
    },
    {
      id: 'unused',
      label: 'Unused visits',
      cell: (m) => ({
        text: m.visitsTotal - m.visitsUsed > 0 ? `${m.visitsTotal - m.visitsUsed} to book` : 'None',
      }),
    },
  ];
  const list: ListRegion = {
    title: 'Renewing soon',
    status: stateOf(q.members, q.error),
    what: 'the renewals',
    emptyText: `No plan ends in the next ${RENEWAL_DAYS} days.`,
    failedText: q.error ?? 'The renewals didn’t load.',
    onRetry: () => void q.reload(),
    children: q.members && <DataTable columns={columns} rows={q.members} more={moreOf(q)} empty="" />,
  };
  return (
    <ListPage
      head={{
        ...head,
        ...(holdsAny(held, EXPIRE_PERMS)
          ? {
              actions: (
                <Button isBusy={busy} onPress={() => void expire()}>
                  {busy ? 'Ending…' : 'End the plans that are over'}
                </Button>
              ),
            }
          : {}),
      }}
      notice={
        notice || why ? (
          <>
            {notice}
            {why && (
              <p className="bp-member-bad" role="alert">
                {why}
              </p>
            )}
          </>
        ) : null
      }
      // only while there is someone to call (the count beside Renewals says how many)
      list={{ ...list, ...(atRisk ? { before: <AtRisk {...props} /> } : {}) }}
    />
  );
}

export function MembershipsPage({
  nav,
  held: given,
  open,
}: {
  nav: NavProps;
  held: readonly string[];
  /** A membership's renewal notification: Renewals, on its row. */
  open?: { part: 'renewals'; at: number; focus?: string };
}) {
  const [part, setPart] = useState<MembersPart>(open?.part ?? 'members');
  useEffect(() => {
    if (open) setPart(open.part);
  }, [open?.part, open?.at]);
  const [toast, setToast] = useToast();
  const here = MEMBER_PARTS.find((p) => p.part === part)!;
  // Only those who book are sent to To book (the button shows to them only, cb38805)
  const books = holdsAny(given, BOOKER_PERMS);
  // While the company mirrors its import source, its memberships, plans and renewals are the source's
  // (st-mirror §2): every part reads them, and none offers a change; the page says so once (BL-088)
  const mirroring = useMirror();
  const held = mirroring ? [] : given;
  // how many renewals are at risk, beside Renewals in the parts (BL-009)
  // the whole list's count, not a page's: one row is enough to read it
  const risk = useQuery<{ total: number }>('memberships.list', { ...AT_RISK_INPUT, limit: 1 });
  const riskWords = atRiskCount(risk.data?.total ?? 0);
  const crumb = usePageCrumb(nav);
  const head: PageHeadProps = {
    ...(crumb ? { crumb } : {}),
    title: here.label,
    lede: part === 'due' && !books ? DUE_LEDE_READ : here.lede,
    tabs: {
      label: 'Memberships',
      items: MEMBER_PARTS.map((p) => ({
        label: p.label,
        href: '#/memberships',
        isCurrent: p.part === part,
        onPress: () => setPart(p.part),
        ...(p.part === 'renewals' && riskWords ? { count: risk.data!.total, countLabel: riskWords } : {}),
      })),
    },
  };
  const notice =
    mirroring || toast ? (
      <>
        {mirroring && <p className="bp-member-quiet">{MIRROR_LINE}</p>}
        {toast && <Notice tone="done" title={toast} />}
      </>
    ) : null;
  const props: Props = {
    today: todayIn(nav.timeZone),
    toast: setToast,
    held,
    head,
    notice,
    ...(part === 'renewals' && open?.focus ? { focus: open.focus } : {}),
  };
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        {part === 'members' && <Members {...props} />}
        {part === 'due' && <VisitsDue {...props} onBook={books ? () => nav.onView('tobook') : null} />}
        {part === 'renewals' && <Renewals {...props} atRisk={!!riskWords} />}
        {/* Blueprint: the plans are Setup's now (the setup audit); a way there */}
        {part === 'plans' && (
          <ListPage
            head={head}
            notice={notice}
            list={{
              title: 'Plans',
              children: (
                <p className="bp-member-quiet" role="note">
                  Membership plans moved to <Link href="#/settings/plans">Setup › Membership plans</Link>.
                </p>
              ),
            }}
          />
        )}
      </main>
    </div>
  );
}
